import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { CheckCircle2, Phone } from 'lucide-react';
export function AccessibilityUpgradesPage() {
  return <div className="flex flex-col min-h-screen pt-24 pb-20" data-id="element-211">
      {/* Hero */}
      <section className="relative bg-gray-50 py-16 md:py-24 overflow-hidden" data-id="element-212">
        <div className="absolute inset-0 z-0" data-id="element-213">
          <img src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=2000" alt="Accessible bathroom" className="w-full h-full object-cover object-center" data-id="element-214" />
          <div className="absolute inset-0 bg-white/85" data-id="element-215"></div>
        </div>

        <div className="container relative z-10 mx-auto px-4 md:px-6" data-id="element-216">
          <div className="max-w-3xl mx-auto text-center" data-id="element-217">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-218">
              Accessibility Upgrades for Northern Indiana Bathrooms
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-219">
              Safe, accessible bathroom modifications for aging in place and
              mobility needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-220">
              <Link to="/contact" data-id="element-221">
                <Button size="lg" data-id="element-222">Get Free Consultation</Button>
              </Link>
              <Button variant="outline" size="lg" data-id="element-223">
                <Phone className="mr-2 h-5 w-5" data-id="element-224" />
                (574) 914-1522
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16" data-id="element-225">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl" data-id="element-226">
          <div className="prose prose-lg max-w-none" data-id="element-227">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-228">
              Creating Safe, Accessible Bathrooms
            </h2>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-229">
              Everyone deserves a bathroom that's safe, comfortable, and easy to
              use. Lucky Duck Bathroom Company specializes in accessibility
              upgrades throughout Northern Indiana, helping homeowners create
              bathrooms that support independent living and accommodate changing
              mobility needs. Whether you're planning for aging in place,
              recovering from injury, or adapting your home for a family member
              with disabilities, we provide thoughtful solutions that combine
              safety with style.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-230">
              Essential Accessibility Features
            </h3>

            <div className="bg-gray-50 p-6 rounded-lg mb-6" data-id="element-231">
              <h4 className="text-xl font-bold text-gray-900 mb-4" data-id="element-232">
                Accessibility Upgrades We Provide:
              </h4>
              <ul className="space-y-3" data-id="element-233">
                {['Walk-in showers with low or zero-threshold entries', 'Grab bars strategically placed for support and stability', 'Built-in or fold-down shower seating', 'Handheld showerheads with adjustable height', 'Non-slip flooring throughout the bathroom', 'Comfort-height toilets for easier transfers', 'Accessible vanities with knee clearance', 'Lever-style faucets and door handles', 'Improved lighting for better visibility', 'Wider doorways for wheelchair access'].map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-234">
                    <CheckCircle2 className="h-6 w-6 text-[#8BA888] shrink-0 mt-0.5" data-id="element-235" />
                    <span className="text-gray-700" data-id="element-236">{item}</span>
                  </li>)}
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-237">
              Walk-In Showers and Tub-to-Shower Conversions
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-238">
              Replacing a traditional bathtub with a walk-in shower eliminates
              the dangerous step-over barrier that causes many bathroom falls.
              We design walk-in showers with low-profile or curbless entries
              that accommodate walkers and wheelchairs while maintaining proper
              water containment. These showers can include built-in benches for
              seated showering, multiple grab bars positioned for safe entry and
              exit, and handheld showerheads that make bathing easier and more
              comfortable.
            </p>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-239">
              Modern accessible showers don't sacrifice style for function. We
              offer beautiful tile options, frameless glass doors, and
              contemporary fixtures that create spa-like environments while
              meeting accessibility needs. The result is a bathroom that's both
              safe and attractive—a space you'll feel proud to show guests.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-240">
              Grab Bars and Safety Features
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-241">
              Properly installed grab bars provide crucial support for
              maintaining balance and preventing falls. We install grab bars in
              strategic locations: near toilets for safe transfers, inside
              showers for stability while bathing, and along walls for support
              when moving around the bathroom. Modern grab bars come in various
              styles and finishes that coordinate with your bathroom décor—they
              no longer look institutional or clinical.
            </p>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-242">
              Installation matters significantly for grab bar safety. We anchor
              grab bars to solid backing or structural framing, ensuring they
              can support the weight and force of someone catching themselves
              during a fall. This professional installation provides the
              security and peace of mind that adhesive or surface-mounted
              options simply cannot deliver.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-243">
              Aging in Place Planning
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-244">
              Aging in place means making modifications that allow you to remain
              safely in your home as mobility changes over time. We help
              homeowners plan bathroom renovations that address current needs
              while anticipating future requirements. This might include
              installing blocking behind walls for future grab bar placement,
              choosing slip-resistant flooring, ensuring adequate maneuvering
              space, and selecting fixtures that are easy to operate with
              limited dexterity.
            </p>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-245">
              These proactive modifications are much easier and more
              cost-effective to incorporate during a planned renovation than to
              retrofit later. We work with you to understand your long-term
              goals and create solutions that provide safety and independence
              for years to come.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-246">
              ADA Compliance and Universal Design
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-247">
              For homeowners requiring ADA-compliant bathrooms, we understand
              the specific requirements for clearances, fixture heights, grab
              bar placement, and other accessibility standards. We can design
              and build bathrooms that meet these guidelines while maintaining
              an attractive, residential feel. Universal design principles
              create spaces that work well for people of all ages and
              abilities—features that benefit everyone, not just those with
              mobility challenges.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8" data-id="element-248">
              Why Choose Lucky Duck for Accessibility Upgrades?
            </h3>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-249">
              As a family-owned business serving Northern Indiana, we understand
              the importance of creating bathrooms that support independent
              living and dignity. We approach accessibility projects with
              sensitivity and respect, listening carefully to your needs and
              concerns. Our experienced team has the knowledge to recommend
              effective solutions and the craftsmanship to execute them
              properly.
            </p>

            <p className="text-gray-600 leading-relaxed mb-6" data-id="element-250">
              We serve South Bend, Mishawaka, Elkhart, Goshen, Granger, and
              surrounding communities with honest guidance, quality workmanship,
              and the personalized attention you deserve. Whether you need a few
              targeted modifications or a complete accessible bathroom
              renovation, we're committed to creating a safe, functional space
              that enhances your quality of life.
            </p>

            <div className="bg-[#8BA888]/10 border-l-4 border-[#8BA888] p-6 rounded-r-lg mt-8" data-id="element-251">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-252">
                Ready to Make Your Bathroom Safer and More Accessible?
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-253">
                Contact Lucky Duck Bathroom Company for a free consultation.
                We'll discuss your needs and recommend solutions that work for
                your situation and budget.
              </p>
              <Link to="/contact" data-id="element-254">
                <Button size="lg" data-id="element-255">Schedule Free Consultation</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>;
}